/**
 * Icon component — renders HugeIcons from local @iconify-json/hugeicons data.
 * Zero network requests. All icons bundled locally.
 *
 * Usage:
 *   <Icon name="home-01" />
 *   <Icon name="search-01" size={20} className="text-muted-foreground" />
 *   <DuotoneIcon name="home-01" size={20} />
 *
 * Browse icons at: https://hugeicons.com
 * Icon names use kebab-case e.g. "bell-01", "chart-bar-01", "globe-02"
 */

import { Icon as IconifyIcon, addCollection } from "@iconify/react";
import hugeicons from "@iconify-json/hugeicons/icons.json";
import duotoneData from "../../../public/icon_data/icon-duotone.json";
import { cn } from "@/lib/utils";

// Register the full HugeIcons collection locally — runs once on import
addCollection(hugeicons as Parameters<typeof addCollection>[0]);

interface IconProps {
  /** HugeIcons icon name e.g. "home-01", "bell-02", "chart-bar-01" */
  name: string;
  size?: number;
  className?: string;
  style?: React.CSSProperties;
}

export function Icon({ name, size = 16, className, style }: IconProps) {
  return (
    <IconifyIcon
      icon={`hugeicons:${name}`}
      width={size}
      height={size}
      className={cn("shrink-0", className)}
      style={style}
    />
  );
}

interface DuotoneIconProps {
  /** Duotone icon name from icon-duotone.json e.g. "arrow-down2", "refresh" */
  name: string;
  size?: number;
  className?: string;
  style?: React.CSSProperties;
}

export function DuotoneIcon({ name, size = 16, className, style }: DuotoneIconProps) {
  const aliasMap: Record<string, string> = {
    globe: "global",
    trending: "chart",
    setup: "settings",
    "arrow-left": "arrow-left",
  };

  const resolvedName = aliasMap[name] ?? name;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const iconData = (duotoneData as any).icons?.[resolvedName];

  if (!iconData?.code) {
    return null;
  }

  const viewBox = "0 0 24 24";
  
  return (
    <svg
      viewBox={viewBox}
      width={size}
      height={size}
      className={cn("shrink-0", className)}
      style={style}
      dangerouslySetInnerHTML={{ __html: iconData.code }}
    />
  );
}

// ─── Typed icon name constants for autocomplete ───────────────────────────────
// Add more as needed from https://hugeicons.com

export const HugeIcons = {
  // Navigation
  home: "home-01",
  dashboard: "dashboard-square-01",
  menu: "menu-01",
  close: "cancel-01",
  back: "arrow-left-01",
  forward: "arrow-right-01",

  // Alerts & notifications
  bell: "notification-01",
  bellOff: "notification-off-01",
  alert: "alert-01",
  alertCircle: "alert-circle",
  info: "information-circle",

  // Data & analytics
  chart: "chart-bar-01",
  chartLine: "chart-line-data-01",
  chartPie: "pie-chart-01",
  trending: "trending-up-01",
  trendingDown: "trending-down-01",

  // Competitors & monitoring
  globe: "global-01",
  link: "link-01",
  eye: "eye",
  eyeOff: "eye-off",
  scan: "scan",
  radar: "radar-01",
  target: "target-01",

  // Actions
  add: "add-01",
  remove: "remove-01",
  edit: "edit-01",
  delete: "delete-01",
  copy: "copy-01",
  refresh: "refresh",
  search: "search-01",
  filter: "filter",
  sort: "sort-by-down-01",
  download: "download-01",
  upload: "upload-01",
  send: "sent",

  // Status
  check: "checkmark-circle-01",
  checkSquare: "checkmark-square-01",
  x: "cancel-circle",
  pause: "pause",
  play: "play",
  stop: "stop",

  // UI
  settings: "settings-01",
  user: "user-01",
  users: "user-group",
  logout: "logout-01",
  lock: "lock-01",
  unlock: "lock-unlock-01",
  key: "key-01",
  mail: "mail-01",
  slack: "slack",
  zap: "flash",
  star: "star",
  bookmark: "bookmark-01",
  tag: "tag-01",
  clock: "clock-01",
  calendar: "calendar-01",
  folder: "folder-01",
  file: "file-01",
  code: "source-code-square-01",
  terminal: "computer",
  cpu: "cpu-01",
  database: "database-01",
  server: "server-01",
  cloud: "cloud-01",
  wifi: "wifi-01",

  // Arrows & chevrons
  chevronDown: "arrow-down-01",
  chevronUp: "arrow-up-01",
  chevronLeft: "arrow-left-01",
  chevronRight: "arrow-right-01",
  arrowUp: "arrow-up-02",
  arrowDown: "arrow-down-02",
  externalLink: "square-arrow-up-right",
} as const;

export type HugeIconName = typeof HugeIcons[keyof typeof HugeIcons];
